(function () {
  class BurdlifeAiAssistant {
    constructor(host) {
      this.host = host;
      this.endpoint = (this.host.dataset.endpoint || '').trim();
      this.localEndpoint = (this.host.dataset.localEndpoint || '').trim();
      this.emailEndpoint = (this.host.dataset.emailEndpoint || '').trim();
      this.localEmailEndpoint = (this.host.dataset.localEmailEndpoint || '').trim();
      this.toggleButton = this.host.querySelector('[data-assistant-toggle]');
      this.closeButton = this.host.querySelector('[data-assistant-close]');
      this.panel = this.host.querySelector('[data-assistant-panel]');
      this.messages = this.host.querySelector('[data-assistant-messages]');
      this.form = this.host.querySelector('[data-assistant-form]');
      this.input = this.host.querySelector('[data-assistant-input]');
      this.sendButton = this.host.querySelector('[data-assistant-send]');
      this.copy = JSON.parse(this.host.querySelector('[data-assistant-copy]')?.textContent || '{}');
      this.availability = this.host.querySelector('[data-assistant-availability]');
      this.stopButton = this.host.querySelector('[data-assistant-stop]');
      this.newButton = this.host.querySelector('[data-assistant-new]');
      this.transcript = [];
      this.storageKey = 'burdlife-chat-v2';
      this.status = this.host.querySelector('[data-assistant-status]');
      this.promptButtons = Array.from(this.host.querySelectorAll('[data-assistant-prompt]'));
      this.tourStartButton = this.host.querySelector('[data-assistant-tour-start]');
      this.nudge = this.host.querySelector('[data-assistant-nudge]');
      this.nudgeTourButton = this.host.querySelector('[data-assistant-nudge-tour]');
      this.nudgeCloseButton = this.host.querySelector('[data-assistant-nudge-close]');
      this.tour = this.host.querySelector('[data-assistant-tour]');
      this.tourTitle = this.host.querySelector('[data-assistant-tour-title]');
      this.tourDescription = this.host.querySelector('[data-assistant-tour-description]');
      this.tourProgress = this.host.querySelector('[data-assistant-tour-progress]');
      this.tourBackButton = this.host.querySelector('[data-assistant-tour-back]');
      this.tourEndButton = this.host.querySelector('[data-assistant-tour-end]');
      this.tourNextButton = this.host.querySelector('[data-assistant-tour-next]');
      this.sessionId = this.getSessionId();
      this.conversationId = null;
      this.busy = false;
      this.requestController = null;
      this.tourIndex = 0;
      this.activeTourTarget = null;
      this.recommendations = [];
      this.tourSteps = [
        {
          selector: '[id*="section_custom_content_WWHUx9"]',
          title: 'Start with your style',
          description: 'Shop the current BURDLIFE story, or ask me to find pieces that match your look.'
        },
        {
          selector: '[id*="section_best_seller_motion_home"]',
          title: 'See what everyone loves',
          description: 'Browse best sellers in motion. Ask me to compare pieces, finishes, or gift ideas.'
        },
        {
          selector: '[id*="section_piercing_picks_home"]',
          title: 'Shop Natasha’s picks',
          description: 'Explore founder-picked jewelry and jump directly into piercing availability.'
        },
        {
          selector: '[id*="section_build_stack_home"]',
          title: 'Build a complete ear stack',
          description: 'Choose a curated stack, inspect each placement, and add the matching pieces.'
        },
        {
          selector: '[id*="section_columns_qNAtQy"]',
          title: 'Visit or book a service',
          description: 'Find stores, permanent jewelry, piercings, styling, and private events.'
        }
      ];

      if (!this.toggleButton || !this.panel || !this.form || !this.input) return;

      this.closeButton.addEventListener('click', () => this.closePanel());
      this.panel.addEventListener('cancel', (event) => { event.preventDefault(); this.closePanel(); });
      this.stopButton?.addEventListener('click', () => this.requestController?.abort());
      this.newButton?.addEventListener('click', () => this.resetConversation());
      this.input.addEventListener('input', () => { this.resizeInput(); this.updateSendButton(); this.saveConversation(); });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
          event.preventDefault();
          this.sendMessage(this.input.value);
        }
      });
      this.onViewportChange = () => this.syncViewport();
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.sendMessage(this.input.value);
      });
      if (this.tourStartButton) {
        this.tourStartButton.addEventListener('click', () => this.startTour());
      }
      if (this.nudgeTourButton) {
        this.nudgeTourButton.addEventListener('click', () => this.startTour());
      }
      if (this.nudgeCloseButton) {
        this.nudgeCloseButton.addEventListener('click', () => this.dismissNudge());
      }
      if (this.tourBackButton) {
        this.tourBackButton.addEventListener('click', () => this.previousTourStep());
      }
      if (this.tourEndButton) {
        this.tourEndButton.addEventListener('click', () => this.endTour());
      }
      if (this.tourNextButton) {
        this.tourNextButton.addEventListener('click', () => this.nextTourStep());
      }
      this.promptButtons.forEach((button) => {
        button.addEventListener('click', () => this.sendMessage(button.dataset.assistantPrompt));
      });
      this.host.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.tour && !this.tour.hidden) this.endTour();
      });

      this.applyContextualPrompts();
      this.setupLauncherMotion();
      this.restoreConversation();
      this.resizeInput();
      this.updateSendButton();
      this.setupOverlayCoordination();
      this.setupPurchaseCoordination();
    }

    setupPurchaseCoordination() {
      if (!('IntersectionObserver' in window)) return;
      this.observePurchaseControls = () => {
        this.purchaseObserver?.disconnect();
        this.host.removeAttribute('data-purchase-controls-visible');
        const controls = document.querySelectorAll('.template-product [data-add-to-cart], .template-product .shopify-payment-button, .template-cart [data-cart-checkout-buttons], .template-cart .cart-account-choice');
        if (!controls.length) return;
        const visible = new Set();
        // Reserve the bottom strip for purchase controls before they meet the launcher.
        this.purchaseObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) visible.add(entry.target);
            else visible.delete(entry.target);
          }
          this.host.toggleAttribute('data-purchase-controls-visible', visible.size > 0);
        }, { rootMargin: `-${Math.max(0, window.innerHeight - 120)}px 0px 0px 0px` });
        controls.forEach((control) => this.purchaseObserver.observe(control));
      };
      this.observePurchaseControls();
      window.addEventListener('resize', this.observePurchaseControls);
      document.addEventListener('shopify:section:load', this.observePurchaseControls);
      document.addEventListener('theme:cart:change', this.observePurchaseControls);
    }

    setupOverlayCoordination() {
      // Observe only native overlay state holders, never the full document subtree.
      this.overlaySelector = '.drawer, [data-collection-sidebar], header-search-popdown details, [data-top-link][aria-expanded], .pswp, dialog, [data-popup-container], [role="dialog"][aria-modal="true"]';
      this.overlayOpenSelector = '.drawer.is-open, [data-collection-sidebar].expanded, header-search-popdown details[open], [data-top-link][aria-expanded="true"], .pswp.pswp--open, .pswp.pswp--custom-opening, dialog[open], [data-popup-container].is-open';
      this.overlayNodes = new Set();
      this.overlayStateObserver = new MutationObserver(() => this.syncOverlayState());
      this.refreshOverlayNodes = () => {
        for (const node of this.overlayNodes) {
          if (!node.isConnected) this.overlayNodes.delete(node);
        }
        for (const node of document.querySelectorAll(this.overlaySelector)) {
          if (this.host.contains(node) || this.overlayNodes.has(node)) continue;
          this.overlayNodes.add(node);
          this.overlayStateObserver.observe(node, {
            attributes: true,
            attributeFilter: ['class', 'open', 'aria-expanded', 'hidden', 'style', 'aria-hidden']
          });
        }
        this.syncOverlayState();
      };
      this.scheduleOverlayRefresh = () => {
        if (this.overlayFrame) return;
        this.overlayFrame = requestAnimationFrame(() => {
          this.overlayFrame = null;
          this.refreshOverlayNodes();
        });
      };
      // Quick-add dialogs are appended directly to body. Theme events cover section replacement.
      this.overlayMountObserver = new MutationObserver(this.scheduleOverlayRefresh);
      this.overlayMountObserver.observe(document.body, { childList: true });
      this.overlayEvents = ['theme:cart:open', 'theme:cart:close', 'theme:drawer:open', 'theme:drawer:close', 'theme:scroll:lock', 'theme:scroll:unlock', 'theme:modal:open', 'theme:modal:close', 'theme:popup:open', 'theme:popup:onclose', 'theme:filter:close', 'theme:resize:width', 'shopify:section:load'];
      for (const eventName of this.overlayEvents) {
        document.addEventListener(eventName, this.scheduleOverlayRefresh, true);
      }
      this.refreshOverlayNodes();
    }

    syncOverlayState() {
      const blocked = Array.from(this.overlayNodes).some((node) => {
        if (!node.isConnected || this.host.contains(node)) return false;
        const appModal = node.matches('[role="dialog"][aria-modal="true"]:not(dialog):not([data-popdown]):not(.drawer):not(.pswp):not([data-popup-container])');
        if (appModal) {
          const style = getComputedStyle(node);
          return !node.hidden && node.getAttribute('aria-hidden') !== 'true' && node.getClientRects().length > 0 && style.visibility !== 'hidden' && style.display !== 'none';
        }
        if (!node.matches(this.overlayOpenSelector)) return false;
        // Expanded desktop filter columns do not cover the shopping interface.
        if (node.matches('[data-collection-sidebar]') && window.innerWidth >= 750 && !node.querySelector('[data-collection-sidebar-slide-out]')) return false;
        return true;
      });
      if (blocked === Boolean(this.overlaySuspended)) return;
      this.overlaySuspended = blocked;
      if (blocked) {
        if (!this.panel.hidden) this.closePanel({ restoreFocus: false });
        if (this.tour && !this.tour.hidden) this.endTour({ restoreFocus: false });
      }
      this.host.toggleAttribute('data-overlay-suspended', blocked);
      this.host.inert = blocked;
      document.documentElement.toggleAttribute('data-bl-commerce-overlay', blocked);
      // Restore the launcher only. Reopening chat or stealing focus would interrupt shopping.
    }

    disconnectOverlayCoordination() {
      this.purchaseObserver?.disconnect();
      window.removeEventListener('resize', this.observePurchaseControls);
      document.removeEventListener('shopify:section:load', this.observePurchaseControls);
      document.removeEventListener('theme:cart:change', this.observePurchaseControls);
      this.overlayStateObserver?.disconnect();
      this.overlayMountObserver?.disconnect();
      cancelAnimationFrame(this.overlayFrame);
      for (const eventName of this.overlayEvents || []) {
        document.removeEventListener(eventName, this.scheduleOverlayRefresh, true);
      }
      document.documentElement.removeAttribute('data-bl-commerce-overlay');
    }

    isLocalPreview() {
      return window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';
    }

    resolveEndpoint(kind) {
      const rawEndpoint = kind === 'email'
        ? (this.isLocalPreview() ? this.localEmailEndpoint : this.emailEndpoint)
        : (this.isLocalPreview() ? this.localEndpoint : this.endpoint);
      if (!rawEndpoint) throw new Error(`${kind === 'email' ? 'Email' : 'Assistant'} endpoint is not configured`);

      const endpointUrl = new URL(rawEndpoint, window.location.origin);
      if (endpointUrl.origin === window.location.origin) {
        if (!endpointUrl.pathname.startsWith('/apps/burdlife-assistant/')) {
          throw new Error('Production requests must use the approved storefront app proxy');
        }
        return endpointUrl;
      }

      const localOrigins = new Set(['http://127.0.0.1:9293', 'http://localhost:9293']);
      if (!this.isLocalPreview() || !localOrigins.has(endpointUrl.origin)) {
        throw new Error('Cross-origin assistant requests are only allowed for the local test server');
      }
      return endpointUrl;
    }

    getSessionId() {
      const storageKey = 'burdlife-assistant-session';

      try {
        const existingId = window.sessionStorage.getItem(storageKey);
        if (existingId) return existingId;

        const newId = window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        window.sessionStorage.setItem(storageKey, newId);
        return newId;
      } catch (error) {
        return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }
    }

    togglePanel() {
      if (this.panel.hidden) {
        this.openPanel();
      } else {
        this.closePanel();
      }
    }

    openPanel() {
      if (!this.panel.hidden || this.overlaySuspended) return;
      this.dismissNudge();
      this.panel.hidden = false;
      this.closeButton.setAttribute('autofocus', '');
      this.panel.showModal();
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.toggleButton.setAttribute('aria-expanded', 'true');
      window.visualViewport?.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('scroll', this.onViewportChange);
      window.addEventListener('resize', this.onViewportChange);
      this.syncViewport();
      const target = window.matchMedia('(pointer: fine)').matches ? this.input : this.closeButton;
      target.focus({ preventScroll: true });
      this.messages.scrollTop = this.messages.scrollHeight;
    }

    closePanel({ restoreFocus = true } = {}) {
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.panel.close();
      this.panel.hidden = true;
      document.body.style.overflow = this.previousOverflow || '';
      this.toggleButton.setAttribute('aria-expanded', 'false');
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      if (restoreFocus && !this.overlaySuspended) this.toggleButton.focus({ preventScroll: true });
    }

    syncViewport() {
      const viewport = window.visualViewport;
      this.panel.style.setProperty('--bl-chat-height', `${viewport?.height || window.innerHeight}px`);
      this.panel.style.setProperty('--bl-chat-top', `${viewport?.offsetTop || 0}px`);
      this.panel.classList.toggle('is-keyboard-open', (viewport?.height || window.innerHeight) < 500);
    }

    resizeInput() {
      this.input.style.height = 'auto';
      this.input.style.height = `${Math.max(44, Math.min(this.input.scrollHeight, 96))}px`;
    }

    updateSendButton() {
      this.sendButton.disabled = this.busy || !this.input.value.trim();
    }

    saveConversation() {
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify({
          updated: Date.now(), id: this.conversationId,
          transcript: this.transcript.slice(-16), draft: this.input.value.slice(0, 500)
        }));
      } catch { /* Chat remains usable without storage. */ }
    }

    restoreConversation() {
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(this.storageKey));
        if (!saved || Date.now() - saved.updated > 30 * 60 * 1000 || !Array.isArray(saved.transcript)) return;
        this.conversationId = typeof saved.id === 'string' ? saved.id : null;
        this.input.value = typeof saved.draft === 'string' ? saved.draft.slice(0, 500) : '';
        for (const entry of saved.transcript.slice(-16)) {
          if (!['user', 'assistant'].includes(entry.role) || typeof entry.text !== 'string') continue;
          this.appendMessage(entry.text.slice(0, 4000), entry.role);
          if (entry.role === 'assistant') this.appendActions(entry.actions);
          this.transcript.push(entry);
        }
      } catch { /* Ignore unavailable or invalid storage. */ }
    }

    resetConversation() {
      if (this.busy) return;
      this.conversationId = null;
      this.transcript = [];
      this.input.value = '';
      this.panel.classList.remove('has-conversation');
      this.messages.querySelectorAll('[data-chat-entry], [data-chat-error], .bl-assistant__actions').forEach((node) => node.remove());
      this.status.textContent = '';
      this.availability.textContent = this.copy.subtitle;
      this.saveConversation();
      this.resizeInput();
      this.updateSendButton();
    }

    getPageContext() {
      const data = this.host.dataset;
      const type = data.pageType || '';
      const path = data.pagePath || window.location.pathname;
      const productText = `${data.productHandle || ''} ${data.productType || ''} ${data.pageTitle || ''}`;
      const isService = (type === 'product' && (/appointment|service|booking/i.test(productText) || ['piercing', 'piercing-nashville'].includes(data.productHandle))) ||
        (type === 'page' && /(?:appointments?|book-a|piercing|permanent-jewelry|styling|our-stores|houston|nashville)/i.test(path));
      return {
        page_type: type, page_title: data.pageTitle || '', product_id: data.productId || null,
        product_handle: data.productHandle || '', collection_handle: data.collectionHandle || '',
        page_path: path,
        page_kind: isService ? 'service' : data.giftCard === 'true' ? 'gift_card' : type
      };
    }

    applyContextualPrompts() {
      const context = this.getPageContext();
      const kind = context.page_kind;
      const copy = this.copy.contextual || {};
      const keys = kind === 'service' ? ['book', 'prepare', 'services', 'shop']
        : kind === 'gift_card' ? ['gift_card', 'gift_budget', 'gift_jewelry', 'shipping']
        : kind === 'product' ? ['style', 'details', 'pair', 'shipping']
        : kind === 'collection' ? ['narrow', 'budget', 'gift_jewelry', 'shop']
        : ['shop', 'gift_budget', 'pair', 'book'];
      const defaults = {
        book: 'Help me book a visit', prepare: 'Plan my visit', services: 'Compare services', shop: 'Find jewelry for me',
        gift_card: 'Help me choose an amount', gift_budget: 'Find a gift in my budget', gift_jewelry: 'Explore jewelry gifts',
        shipping: 'Shipping and returns', style: 'How can I style this?', details: 'Tell me about this piece',
        pair: 'Find pieces to wear together', narrow: 'Help me choose', budget: 'Shop within my budget'
      };
      for (let index = 0; index < this.promptButtons.length; index += 1) {
        const button = this.promptButtons[index];
        const key = keys[index];
        button.hidden = !key;
        if (!key) continue;
        const label = copy[key] || defaults[key];
        button.textContent = label;
        // Short labels keep the UI compact; product context travels separately to the server.
        button.dataset.assistantPrompt = label;
      }
      this.host.dataset.pageKind = kind;
      if (this.tourStartButton) this.tourStartButton.hidden = true;
    }

    setupLauncherMotion() {
      if (!this.toggleButton) return;
      this.toggleButton.addEventListener('pointermove', (event) => {
        const bounds = this.toggleButton.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 8;
        const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 8;
        this.toggleButton.style.setProperty('--bl-assistant-look-x', `${x.toFixed(2)}deg`);
        this.toggleButton.style.setProperty('--bl-assistant-look-y', `${(-y).toFixed(2)}deg`);
      });
      this.toggleButton.addEventListener('pointerleave', () => {
        this.toggleButton.style.removeProperty('--bl-assistant-look-x');
        this.toggleButton.style.removeProperty('--bl-assistant-look-y');
      });
    }

    scheduleNudge() {
      if (!this.nudge || this.host.dataset.pageType !== 'index') return;
      try {
        if (window.sessionStorage.getItem('burdlife-assistant-nudge-seen') === 'true') return;
      } catch (error) {
        // Show the nudge when session storage is unavailable.
      }

      window.setTimeout(() => {
        if (!this.panel.hidden || !this.nudge) return;
        this.nudge.hidden = false;
        this.nudge.classList.add('is-visible');
      }, 3200);
    }

    dismissNudge(persist = true) {
      if (!this.nudge) return;
      this.nudge.hidden = true;
      this.nudge.classList.remove('is-visible');
      if (!persist) return;
      try {
        window.sessionStorage.setItem('burdlife-assistant-nudge-seen', 'true');
      } catch (error) {
        // Dismissal still works for the current page without session storage.
      }
    }

    startTour() {
      if (!this.tour) return;
      const availableSteps = [];
      for (const step of this.tourSteps) {
        if (document.querySelector(step.selector)) availableSteps.push(step);
      }
      if (availableSteps.length === 0) {
        this.status.textContent = 'The guided tour is available on the homepage.';
        return;
      }

      this.tourSteps = availableSteps;
      this.tourIndex = 0;
      this.dismissNudge();
      if (!this.panel.hidden) this.closePanel();
      this.host.classList.add('bl-assistant--touring');
      this.tour.hidden = false;
      this.showTourStep();
    }

    showTourStep() {
      const step = this.tourSteps[this.tourIndex];
      if (!step) {
        this.endTour();
        return;
      }

      if (this.activeTourTarget) this.activeTourTarget.classList.remove('bl-assistant-tour-target');
      this.activeTourTarget = document.querySelector(step.selector);
      if (!this.activeTourTarget) {
        this.nextTourStep();
        return;
      }

      this.activeTourTarget.classList.add('bl-assistant-tour-target');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.activeTourTarget.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      this.tourTitle.textContent = step.title;
      this.tourDescription.textContent = step.description;
      this.tourProgress.textContent = `${this.tourIndex + 1} of ${this.tourSteps.length}`;
      this.tourBackButton.disabled = this.tourIndex === 0;
      this.tourNextButton.textContent = this.tourIndex === this.tourSteps.length - 1 ? 'Finish' : 'Next';
      window.requestAnimationFrame(() => this.tourNextButton.focus());
    }

    previousTourStep() {
      if (this.tourIndex === 0) return;
      this.tourIndex -= 1;
      this.showTourStep();
    }

    nextTourStep() {
      if (this.tourIndex >= this.tourSteps.length - 1) {
        this.endTour();
        return;
      }
      this.tourIndex += 1;
      this.showTourStep();
    }

    endTour({ restoreFocus = true } = {}) {
      if (this.activeTourTarget) this.activeTourTarget.classList.remove('bl-assistant-tour-target');
      this.activeTourTarget = null;
      if (this.tour) this.tour.hidden = true;
      this.host.classList.remove('bl-assistant--touring');
      if (restoreFocus && !this.overlaySuspended) this.toggleButton.focus();
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      this.updateSendButton();
      if (this.stopButton) this.stopButton.hidden = !isBusy;
      this.sendButton.hidden = isBusy;
      if (this.newButton) this.newButton.disabled = isBusy;
      this.promptButtons.forEach((button) => { button.disabled = isBusy; });
      if (isBusy) {
        this.status.textContent = this.copy.thinking;
        this.availability.textContent = this.copy.thinking;
      }
    }

    appendMessage(text, role) {
      const message = document.createElement('div');
      message.className = `bl-assistant__message bl-assistant__message--${role}`;
      message.textContent = text;
      message.dataset.chatEntry = 'true';
      if (role === 'user') this.panel.classList.add('has-conversation');
      this.messages.appendChild(message);
      this.messages.scrollTop = this.messages.scrollHeight;
      return message;
    }

    appendActions(actions) {
      if (!Array.isArray(actions) || actions.length === 0) return;
      const shouldScroll = this.messages.scrollHeight - this.messages.scrollTop - this.messages.clientHeight < 100;

      const container = document.createElement('div');
      container.className = 'bl-assistant__actions';
      container.setAttribute('role', 'group');
      container.setAttribute('aria-label', this.copy.links);
      this.recommendations = [];

      actions.slice(0, 4).forEach((action) => {
        if (!action || typeof action.label !== 'string' || typeof action.url !== 'string') return;
        if (!action.label.trim()) return;

        try {
          const url = new URL(action.url, window.location.origin);
          if (url.origin !== window.location.origin || !/^\/(products|collections|pages|policies|account)(\/|$)/.test(url.pathname)) return;

          const link = document.createElement('a');
          link.className = url.pathname.startsWith('/products/') ? 'bl-assistant__product-card' : 'bl-assistant__help-link';
          link.href = url.href;

          if (typeof action.image === 'string' && /^https:\/\/cdn\.shopify\.com\//.test(action.image)) {
            const image = document.createElement('img');
            image.src = action.image;
            image.alt = '';
            image.width = 72;
            image.height = 72;
            image.loading = 'lazy';
            link.appendChild(image);
          }

          const copy = document.createElement('span');
          const title = document.createElement('strong');
          title.textContent = action.label.trim().slice(0, 80);
          copy.appendChild(title);
          if (typeof action.price === 'string' && action.price.trim()) {
            const price = document.createElement('small');
            price.textContent = action.price.trim().slice(0, 24);
            copy.appendChild(price);
          }
          link.appendChild(copy);
          container.appendChild(link);
          this.recommendations.push({
            label: action.label.trim().slice(0, 80),
            url: url.pathname,
            price: typeof action.price === 'string' ? action.price.trim().slice(0, 24) : ''
          });
        } catch (error) {
          // Ignore malformed URLs supplied by the endpoint.
        }
      });

      if (container.childElementCount > 0) {
        this.messages.appendChild(container);
        if (this.emailPanel) this.emailPanel.hidden = false;
        if (shouldScroll) this.messages.scrollTop = this.messages.scrollHeight;
      }
    }

    async sendMessage(rawMessage, retry = false) {
      const message = String(rawMessage || '').trim().slice(0, 500);
      if (!message || this.busy) return;
      this.messages.querySelector('[data-chat-error]')?.remove();
      if (!retry) {
        this.appendMessage(message, 'user');
        this.transcript.push({ role: 'user', text: message });
        this.input.value = '';
        this.resizeInput();
      }
      this.saveConversation();
      this.setBusy(true);
      const controller = new AbortController();
      this.requestController = controller;
      let timedOut = false;
      const timeout = window.setTimeout(() => { timedOut = true; controller.abort(); }, 22000);
      try {
        const endpointUrl = this.resolveEndpoint('chat');
        const response = await fetch(endpointUrl.href, {
          method: 'POST',
          signal: controller.signal,
          credentials: endpointUrl.origin === window.location.origin ? 'same-origin' : 'omit',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            message, session_id: this.sessionId, conversation_id: this.conversationId,
            context: this.getPageContext()
          })
        });
        if (!response.ok) throw Object.assign(new Error('Request failed'), { status: response.status });
        const data = await response.json();
        if (typeof data?.reply !== 'string' || !data.reply.trim()) throw new Error('Empty reply');
        this.conversationId = typeof data.conversation_id === 'string' ? data.conversation_id : this.conversationId;
        const reply = data.reply.trim().slice(0, 4000);
        const wasNearBottom = this.messages.scrollHeight - this.messages.scrollTop - this.messages.clientHeight < 100;
        const oldScroll = this.messages.scrollTop;
        this.appendMessage(reply, 'assistant');
        this.appendActions(data.actions);
        if (!wasNearBottom) this.messages.scrollTop = oldScroll;
        this.transcript.push({ role: 'assistant', text: reply, actions: Array.isArray(data.actions) ? data.actions.slice(0, 4) : [] });
        this.saveConversation();
        this.status.textContent = '';
        this.availability.textContent = data.mode === 'guide' ? (this.copy.contextual?.guide || 'Store guide') : data.mode === 'catalog' ? this.copy.catalog : this.copy.subtitle;
      } catch (error) {
        const text = timedOut ? this.copy.timed_out : controller.signal.aborted ? this.copy.stopped : error.status === 429 ? this.copy.rate_limited : this.copy.failed;
        const notice = document.createElement('div');
        notice.className = 'bl-assistant__error';
        notice.dataset.chatError = 'true';
        notice.setAttribute('role', 'status');
        const description = document.createElement('p');
        description.textContent = text;
        const retryButton = document.createElement('button');
        retryButton.type = 'button';
        retryButton.textContent = this.copy.retry;
        retryButton.addEventListener('click', () => this.sendMessage(message, true));
        const support = document.createElement('a');
        support.href = '/pages/contact-us';
        support.textContent = this.copy.contact;
        const browse = document.createElement('a');
        const isService = this.getPageContext().page_kind === 'service' || /book|appointment|pierc|permanent jewelry/i.test(message);
        browse.href = isService ? '/pages/book-a-burdlife-service' : '/collections/all';
        browse.textContent = isService ? (this.copy.contextual?.browse_booking || 'Browse appointments') : (this.copy.contextual?.browse_jewelry || 'Browse jewelry');
        notice.append(description, retryButton, browse, support);
        this.messages.appendChild(notice);
        this.messages.scrollTop = this.messages.scrollHeight;
        this.status.textContent = '';
        this.availability.textContent = this.copy.unavailable;
      } finally {
        window.clearTimeout(timeout);
        this.requestController = null;
        this.setBusy(false);
      }
    }

  }

  function initializeAssistants(root) {
    const assistants = [];

    if (root.matches && root.matches('burdlife-ai-assistant')) assistants.push(root);
    root.querySelectorAll('burdlife-ai-assistant').forEach((assistant) => assistants.push(assistant));

    assistants.forEach((assistant) => {
      if (assistant.burdlifeAssistantController) return;
      const controller = new BurdlifeAiAssistant(assistant);
      assistant.burdlifeAssistantController = controller;
      assistant.dataset.assistantInitialized = 'true';
    });

    const offerTabs = [];
    if (root.matches && root.matches('[data-new-member-tab]')) offerTabs.push(root);
    root.querySelectorAll('[data-new-member-tab]').forEach((tab) => offerTabs.push(tab));

    offerTabs.forEach((tab) => {
      if (tab.dataset.offerInitialized === 'true') return;
      tab.dataset.offerInitialized = 'true';

      const closeButton = tab.querySelector('[data-new-member-tab-close]');
      const storageKey = 'burdlife-new-member-offer-dismissed';

      try {
        if (window.sessionStorage.getItem(storageKey) === 'true') tab.hidden = true;
      } catch (error) {
        // The offer remains visible when session storage is unavailable.
      }

      if (!closeButton) return;
      closeButton.addEventListener('click', () => {
        tab.hidden = true;
        try {
          window.sessionStorage.setItem(storageKey, 'true');
        } catch (error) {
          // Dismissal still works for the current page without session storage.
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAssistants(document));
  } else {
    initializeAssistants(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeAssistants(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const assistant of event.target.querySelectorAll('burdlife-ai-assistant')) {
      const controller = assistant.burdlifeAssistantController;
      if (!controller) continue;
      if (!controller.panel.hidden) controller.closePanel({ restoreFocus: false });
      controller.disconnectOverlayCoordination();
    }
  });

  if (!window.burdlifeAssistantToggleDelegated) {
    window.burdlifeAssistantToggleDelegated = true;
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-assistant-toggle]');
      if (!toggle) return;

      const assistant = toggle.closest('burdlife-ai-assistant');
      if (!assistant) return;
      if (!assistant.burdlifeAssistantController) initializeAssistants(assistant);

      const controller = assistant.burdlifeAssistantController;
      if (controller) controller.togglePanel();
    });
  }
})();
